import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { 
  KnowledgeCategoryService, 
  KnowledgeCategory, 
  KnowledgeCategoryLesson 
} from '../../services/knowledgeCategoryService';
import { LessonSummary } from '../../services/lessonServiceTypes';
import PageHeader from '../common/PageHeader';
import GenericHistorySelector from '../shared/GenericHistorySelector';
import GenericPastLessons from '../shared/GenericPastLessons';
import GenericLessonViewer from '../shared/GenericLessonViewer';
import ImageGeneratorModal from '../shared/ImageGeneratorModal';
import CategoryForm from './CategoryForm';
import './CategoryLessonView.css';
import { trackAction } from '../../services/activityService';

interface CategoryLessonViewProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type LoadingStage = 'idle' | 'text' | 'vocabulary' | 'image';
type ViewMode = 'current' | 'past-list' | 'past-lesson';

const CategoryLessonView: React.FC<CategoryLessonViewProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<KnowledgeCategory | null>(null);
  const [lesson, setLesson] = useState<KnowledgeCategoryLesson | null>(null);
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [selectedPastLesson, setSelectedPastLesson] = useState<KnowledgeCategoryLesson | null>(null);
  const [showHistorySelector, setShowHistorySelector] = useState(false);
  const [pastLessonsList, setPastLessonsList] = useState<LessonSummary[]>([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showImageGeneratorModal, setShowImageGeneratorModal] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [isGeneratingImageOnly, setIsGeneratingImageOnly] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load category and latest lesson on mount
  useEffect(() => {
    if (categoryId) {
      loadCategoryAndLesson();
    }
  }, [categoryId]);

  const loadCategoryAndLesson = async () => {
    try {
      setLoadingStage('text');
      const cat = await KnowledgeCategoryService.getCategoryById(categoryId!);
      setCategory(cat);

      // Load latest lesson
      const result = await KnowledgeCategoryService.getPastLessons(cat.category_slug, 1);
      if (result.lessons.length > 0) {
        const latestLesson = await KnowledgeCategoryService.getLessonById(result.lessons[0].lesson_id);
        setLesson(latestLesson);
      }
      setLoadingStage('idle');
    } catch (err) {
      console.error('Error loading category:', err);
      setError(err instanceof Error ? err.message : 'Failed to load category');
      setLoadingStage('idle');
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleOpenGenerateLesson = async () => {
    try {
      setShowMenu(false);
      if (!category) return;

      // Fetch past lessons for the selector
      const result = await KnowledgeCategoryService.getPastLessons(category.category_slug, 100);
      setPastLessonsList(result.lessons);
      setShowHistorySelector(true);
    } catch (err) {
      console.error('Error loading past lessons:', err);
      setPastLessonsList([]);
      setShowHistorySelector(true);
    }
  };

  const handleGenerateLesson = async (selectedIds: string[], customInstruction: string, generateImage?: boolean) => {
    try {
      if (!categoryId) return;

      setShowHistorySelector(false);
      setLoadingStage('text');
      setError(null);
      
      const newLesson = await KnowledgeCategoryService.generateLesson(
        categoryId,
        selectedIds,
        customInstruction || undefined,
        (stage) => {
          setLoadingStage(stage);
        }
      );
      
      setLesson(newLesson);
      
      // Generate image if requested
      if (generateImage && newLesson.lesson_id) {
        try {
          setLoadingStage('image');
          
          // Use custom instruction or lesson objective as prompt
          const imagePrompt = customInstruction || newLesson.lesson_objective;
          
          // Generate image
          const imageData = await KnowledgeCategoryService.generateLessonImage(
            newLesson.lesson_id,
            imagePrompt
          );
          
          // Upload to Firebase and save URL
          const imageUrl = await KnowledgeCategoryService.uploadLessonImage(
            newLesson.lesson_id,
            imageData.image,
            imageData.mimeType,
            imagePrompt
          );
          
          // Update lesson with image URL
          newLesson.image_url = imageUrl;
          newLesson.image_prompt = imagePrompt;
          setLesson({ ...newLesson });
        } catch (imageErr) {
          console.error('Error generating image:', imageErr);
          // Don't throw - lesson was already created successfully
          setError('Lesson created, but image generation failed. You can try again later.');
        }
      }
      
      setLoadingStage('idle');
      setViewMode('current');
      trackAction(user, 'LESSON_CREATE', {
        categoryId: categoryId,
        lessonId: newLesson.lesson_id,
        title: newLesson.lesson_name,
      });
    } catch (err) {
      console.error('Error generating lesson:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate lesson. Please try again.');
      setLoadingStage('idle');
    }
  };

  const handleOpenPastLessons = () => {
    setShowMenu(false);
    setViewMode('past-list');
  };

  const handleOpenEditCategory = () => {
    setShowMenu(false);
    setShowCategoryForm(true);
  };

  const handleOpenGenerateImage = () => {
    setShowMenu(false);
    if (lesson) {
      setImagePrompt(lesson.lesson_objective || '');
      setShowImageGeneratorModal(true);
    }
  };

  const handleGenerateImageOnly = async (prompt: string, provider?: 'gemini' | 'grok') => {
    if (!lesson?.lesson_id || !prompt.trim()) return;

    try {
      setIsGeneratingImageOnly(true);
      setError(null);

      // Generate image
      const imageData = await KnowledgeCategoryService.generateLessonImage(
        lesson.lesson_id,
        prompt.trim(),
        provider
      );

      // Upload to Firebase and save URL
      const imageUrl = await KnowledgeCategoryService.uploadLessonImage(
        lesson.lesson_id,
        imageData.image,
        imageData.mimeType,
        prompt.trim()
      );

      // Update lesson with image URL
      const updatedLesson = { ...lesson, image_url: imageUrl, image_prompt: prompt.trim() };
      setLesson(updatedLesson);
      
      setShowImageGeneratorModal(false);
      setImagePrompt('');
    } catch (err) {
      console.error('Error generating image:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImageOnly(false);
    }
  };

  const handleCategoryFormClose = () => {
    setShowCategoryForm(false);
  };

  const handleCategoryFormSuccess = async () => {
    setShowCategoryForm(false);
    // Reload category to get updated data
    if (categoryId) {
      try {
        const cat = await KnowledgeCategoryService.getCategoryById(categoryId);
        setCategory(cat);
      } catch (err) {
        console.error('Error reloading category:', err);
      }
    }
  };

  const handleSelectPastLesson = (pastLesson: KnowledgeCategoryLesson) => {
    setSelectedPastLesson(pastLesson);
    setViewMode('past-lesson');
  };

  const handleBackToCurrentLesson = () => {
    setViewMode('current');
    setSelectedPastLesson(null);
  };

  const handleBackToCategories = () => {
    navigate('/knowledge');
  };

  // Get the current lesson being displayed
  const getCurrentLesson = () => {
    if (viewMode === 'past-lesson' && selectedPastLesson) {
      return selectedPastLesson;
    }
    return lesson;
  };

  // Determine if current user owns the category
  const isOwner = (): boolean => {
    if (!user || !category) return false;
    return String(category.user_id) === String(user.id);
  };

  // Determine current lesson and view state
  const currentLesson = getCurrentLesson();
  const showBackButton = viewMode === 'past-lesson';
  const categoryOwned = isOwner();

  return (
    <div className="category-lesson-view-container">
      {/* If in past lessons list view, show GenericPastLessons component */}
      {viewMode === 'past-list' && category && (() => {
        const lessonService = KnowledgeCategoryService.createLessonService(category.category_slug);
        return (
          <GenericPastLessons
            service={lessonService}
            onSelectLesson={handleSelectPastLesson}
            onClose={handleBackToCurrentLesson}
            title={`${category.category_name} - Past Lessons`}
          />
        );
      })()}

      {/* Main lesson view */}
      {viewMode !== 'past-list' && (
        <>
      {/* Page Header */}
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          <button 
            className="page-header-icon-btn category-lesson-back-btn"
            onClick={showBackButton ? handleBackToCurrentLesson : handleBackToCategories}
            aria-label="Back"
          >
            ←
          </button>
        }
        centerContent={
          <h1 className="page-header-center-title">
            {category?.category_name || 'Loading...'}
            {!categoryOwned && category && (
              <span className="category-lesson-readonly-badge"> (Read-only)</span>
            )}
          </h1>
        }
        rightActions={
          <div className="category-lesson-menu-container" ref={menuRef}>
            <button 
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
              disabled={loadingStage !== 'idle' || !category}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="category-lesson-menu-dropdown">
                {categoryOwned && (
                  <>
                    <button
                      className="category-lesson-menu-item"
                      onClick={handleOpenGenerateLesson}
                    >
                      ✨ Generate New Lesson
                    </button>
                    <button
                      className="category-lesson-menu-item"
                      onClick={handleOpenGenerateImage}
                      disabled={!lesson}
                    >
                      🖼️ Generate Image
                    </button>
                    <button
                      className="category-lesson-menu-item"
                      onClick={handleOpenEditCategory}
                    >
                      ✎ Edit Category
                    </button>
                  </>
                )}
                <button
                  className="category-lesson-menu-item"
                  onClick={handleOpenPastLessons}
                >
                  📚 Past Lessons
                </button>
              </div>
            )}
          </div>
        }
      />

      {/* Main Content */}
      <div className="category-lesson-main-content">
        {/* Loading State */}
        {loadingStage !== 'idle' && (
          <div className="category-lesson-loading">
            <div className="category-lesson-spinner-large"></div>
            <p className="category-lesson-loading-text">
              {loadingStage === 'text' 
                ? 'Generating lesson...' 
                : loadingStage === 'image'
                ? 'Generating image...'
                : 'Processing content...'}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && loadingStage === 'idle' && (
          <div className="category-lesson-error">
            <p>{error}</p>
          </div>
        )}

        {/* Lesson Content */}
        {currentLesson && loadingStage === 'idle' && (
          <GenericLessonViewer
            lesson={currentLesson}
            lessonObjective={currentLesson.lesson_objective || 'No objective specified'}
            moduleName="Knowledge"
            user={user}
            lessonContent={currentLesson.lesson_content}
            imageUrl={currentLesson.image_url}
          />
        )}

        {/* Welcome Message */}
        {!currentLesson && loadingStage === 'idle' && !error && category && (
          <div className="category-lesson-welcome">
            <div className="category-lesson-welcome-icon">🧠</div>
            <h2>Welcome to {category.category_name}</h2>
            {categoryOwned ? (
              <p>Click the menu (⋮) in the top right and select "Generate New Lesson" to create your first AI-powered lesson.</p>
            ) : (
              <p>This is a read-only category. Browse past lessons to learn from {category.owner_display_name || category.owner_username || 'the owner'}'s content.</p>
            )}
            {category.description && (
              <div className="category-lesson-welcome-description">
                <p>{category.description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* History Selector Modal */}
      <GenericHistorySelector
        isOpen={showHistorySelector}
        pastLessons={pastLessonsList}
        onGenerate={handleGenerateLesson}
        onClose={() => setShowHistorySelector(false)}
        title={`Generate ${category?.category_name} Lesson`}
        customInstructionPlaceholder="e.g., Focus on advanced concepts, practical examples..."
        showImageOption={true}
      />

      {/* Category Edit Form Modal */}
      {showCategoryForm && category && (
        <CategoryForm
          category={category}
          onClose={handleCategoryFormClose}
          onSuccess={handleCategoryFormSuccess}
        />
      )}

      {/* Image Generator Modal */}
      <ImageGeneratorModal
        isOpen={showImageGeneratorModal}
        onClose={() => setShowImageGeneratorModal(false)}
        onGenerate={handleGenerateImageOnly}
        initialPrompt={imagePrompt}
        isGenerating={isGeneratingImageOnly}
        hintText="Provide a detailed description of the image you want to generate for this lesson."
      />
        </>
      )}
    </div>
  );
};

export default CategoryLessonView;

